import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Activity, Sparkles } from 'lucide-react';

interface TickerItem {
  id: string;
  name: string;
  nameEn: string;
  value: string;
  change: number;
  suffix?: string;
  prefix?: string;
  isRateOrYield?: boolean;
}

export function LandingLiveTicker({ uiLanguage = 'ko' }: { uiLanguage?: 'ko' | 'en' }) {
  const [items, setItems] = useState<TickerItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    async function fetchIndicators() {
      try {
        const res = await fetch('/api/macro/indicators?market=US', {
          signal: controller.signal
        });
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        const data = await res.json() as any;

        if (!isMounted) return;

        const formatNum = (val: any, decimals = 2) => {
          if (val === undefined || val === null || isNaN(Number(val))) return '---';
          return Number(val).toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
          });
        };

        const rawList: TickerItem[] = [
          {
            id: 'sp500',
            name: 'S&P 500',
            nameEn: 'S&P 500',
            value: formatNum(data.sp500?.value, 1),
            change: Number(data.sp500?.change) || 0
          },
          {
            id: 'nasdaq',
            name: '나스닥 종합',
            nameEn: 'NASDAQ',
            value: formatNum(data.nasdaq?.value, 1),
            change: Number(data.nasdaq?.change) || 0
          },
          {
            id: 'kospi',
            name: '코스피',
            nameEn: 'KOSPI',
            value: formatNum(data.kospi?.value, 2),
            change: Number(data.kospi?.change) || 0
          },
          {
            id: 'kosdaq',
            name: '코스닥',
            nameEn: 'KOSDAQ',
            value: formatNum(data.kosdaq?.value, 2),
            change: Number(data.kosdaq?.change) || 0
          },
          {
            id: 'usdkrw',
            name: '원/달러 환율',
            nameEn: 'USD/KRW',
            value: formatNum(data.usdkrw?.value, 1),
            prefix: '₩',
            change: Number(data.usdkrw?.change) || 0
          },
          {
            id: 'treasury10Y',
            name: '미 10년물 국채',
            nameEn: 'US 10Y Yield',
            value: formatNum(data.government?.treasury10Y?.value || data.treasury10Y?.value || data.treasury10Y, 2),
            suffix: '%',
            isRateOrYield: true,
            change: Number(data.government?.treasury10Y?.change || data.treasury10Y?.change) || 0
          },
          {
            id: 'vix',
            name: 'VIX 변동성',
            nameEn: 'VIX Index',
            value: formatNum(data.vix?.value, 2),
            change: Number(data.vix?.change) || 0
          },
          {
            id: 'bitcoin',
            name: '비트코인',
            nameEn: 'Bitcoin',
            value: formatNum(data.bitcoin?.value, 0),
            prefix: '$',
            change: Number(data.bitcoin?.change) || 0
          },
          {
            id: 'gold',
            name: '금 (Gold)',
            nameEn: 'Gold Futures',
            value: formatNum(data.gold?.value, 1),
            prefix: '$',
            change: Number(data.gold?.change) || 0
          },
          {
            id: 'fearGreed',
            name: '공포·탐욕 지수',
            nameEn: 'Fear & Greed',
            value: data.fearGreed?.value ? `${data.fearGreed.value} (${data.fearGreed.rating || 'Neutral'})` : '50 (Neutral)',
            change: Number(data.fearGreed?.change) || 0
          }
        ];

        setItems(rawList);
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.warn('[LandingLiveTicker] Failed to fetch live indicators:', err);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    fetchIndicators();
    const interval = setInterval(fetchIndicators, 60000);

    return () => {
      isMounted = false;
      controller.abort();
      clearInterval(interval);
    };
  }, []);

  if (isLoading && items.length === 0) {
    return (
      <div className="w-full bg-zinc-950/90 border-y border-white/5 py-3 px-4 flex items-center justify-center gap-3 text-xs text-zinc-400 font-mono">
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
        <span>Synchronizing Real-Time Macro Telemetry...</span>
      </div>
    );
  }

  const displayItems = [...items, ...items];

  return (
    <div className="w-full bg-zinc-950/90 border-y border-white/10 backdrop-blur-md overflow-hidden relative group select-none">
      {/* Edge Gradients */}
      <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-zinc-950 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-zinc-950 to-transparent z-10 pointer-events-none" />

      <div className="flex items-center">
        {/* Live Badge */}
        <div className="hidden sm:flex items-center gap-2 px-4 py-2.5 bg-zinc-900 border-r border-white/10 text-emerald-400 text-[11px] font-bold tracking-wider uppercase shrink-0 z-20">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="font-mono">LIVE MACRO</span>
        </div>

        {/* Ticker marquee */}
        <div className="flex overflow-x-hidden whitespace-nowrap py-2.5">
          <div className="flex gap-8 items-center animate-marquee group-hover:[animation-play-state:paused]">
            {displayItems.map((item, idx) => {
              const isPositive = item.change > 0;
              const isZero = item.change === 0;

              return (
                <div
                  key={`${item.id}-${idx}`}
                  className="flex items-center gap-2 font-mono text-xs shrink-0 px-2 py-1 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <span className="text-zinc-400 font-sans font-medium text-[11px]">
                    {uiLanguage === 'ko' ? item.name : item.nameEn}
                  </span>
                  <span className="text-white font-bold tracking-tight">
                    {item.prefix}{item.value}{item.suffix}
                  </span>
                  <span
                    className={`flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${
                      isZero
                        ? 'text-zinc-400 bg-zinc-800/50'
                        : isPositive
                        ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20'
                        : 'text-rose-400 bg-rose-500/10 border border-rose-500/20'
                    }`}
                  >
                    {!isZero && (
                      isPositive ? (
                        <TrendingUp className="w-2.5 h-2.5" />
                      ) : (
                        <TrendingDown className="w-2.5 h-2.5" />
                      )
                    )}
                    {isPositive ? '+' : ''}{item.change.toFixed(2)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
